import { registerComponent, asyncRequest, debounce, loading } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "cart-items",
  class CartItems extends WYATTElement {
    SECTIONS_TO_RENDER = [
      {
        name: "cart-count",
        liveRegion: "cart-count",
      },
      {
        name: "cart",
        liveRegion: "cart-items",
      },
      {
        name: "cart",
        liveRegion: "cart-totals",
      },
      {
        name: "cart",
        liveRegion: "cart-checkout",
      },
      {
        name: "cart",
        liveRegion: "cart-recommendations",
      },
    ];

    constructor() {
      super();

      this.removeButtons = this.querySelectorAll("[data-remove-item-button]");
      this.quantityInputs = this.querySelectorAll('[name="updates[]"]');
      this.addOnCheckboxes = this.querySelectorAll("[name*='add-on']");
      this.currentItemCount = Array.from(
        this.querySelectorAll('[name="updates[]"]'),
      ).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0,
      );

      this.asyncMessageOptions = {
        minDelay: 6000,
        appendPoint:
          '[data-mini-cart][active="true"] [data-dialog-messages], #page-messages',
      };

      this.asyncSectionsBody = {
        sections: this.getSectionNames(),
        sections_url: window.location.pathname,
      };

      this.removeButtons.forEach((button) => {
        button.addEventListener("click", this.remove.bind(this));
      });

      const debouncedQuantityChangeHandler = debounce((event) => {
        this.handleQuantityChange(event);
      }, 300);

      this.quantityInputs.forEach((input) => {
        input.addEventListener("change", debouncedQuantityChangeHandler);
      });

      this.addOnCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener("change", this.handleAddOnChange.bind(this));
      });

      this.subscribe(
        WYATT.PubSub.events.lineItemNoteChange,
        this.handleNoteChange.bind(this),
      );
    }

    remove(event) {
      const line = event.currentTarget.dataset.removeItemButton;

      event.preventDefault();

      this.changeItem(line, { quantity: 0 });
    }

    handleQuantityChange(event) {
      const { target } = event;
      const { lineItemKey, addOnItemKey, addOnMultiplier } = target.dataset;
      const quantity = target.value;

      const updates = {
        [lineItemKey]: quantity,
      };

      if (addOnItemKey) {
        updates[addOnItemKey] =
          parseInt(target.value, 10) * parseInt(addOnMultiplier, 10);
      }

      this.updateItem(updates);
    }

    handleAddOnChange(event) {
      if (event.target.checked) {
        this.addAddOnToCart(event.currentTarget);
      } else {
        this.removeAddOnFromCart(event.currentTarget);
      }
    }

    handleNoteChange(event) {
      const { lineItemIndex, notes, quantity } = event.detail;
      this.changeItem(lineItemIndex, {
        quantity,
        properties: { Notes: notes },
      });
    }

    getSectionNames() {
      return [
        ...new Set(this.SECTIONS_TO_RENDER.map((section) => section.name)),
      ];
    }

    /*
      We need both changeItem() and updateItem() functions because:
      changeItem() cannot handle multiple line items being updated at once which is necessary for updating add-ons, but updateItem() can.
      updateItem() cannot handle adding properties to line items, but changeItem() can.
    */
    async changeItem(line, data) {
      const body = {
        line,
        ...data,
        ...this.asyncSectionsBody,
      };
      const options = {
        ...WYATT.config.fetch.post,
        ...{ body: JSON.stringify(body) },
      };

      loading.add(this);

      const response = await asyncRequest(
        WYATT.routes.cart_change_url,
        options,
        {
          messageOptions: this.asyncMessageOptions,
        },
      );

      if (response) {
        this.handleSuccessfulUpdate(response);
      }

      loading.remove(this);
    }

    async updateItem(updates) {
      const body = {
        updates,
        ...this.asyncSectionsBody,
      };

      const options = {
        ...WYATT.config.fetch.post,
        ...{ body: JSON.stringify(body) },
      };

      loading.add(this);

      const response = await asyncRequest(
        WYATT.routes.cart_update_url,
        options,
        {
          messageOptions: this.asyncMessageOptions,
        },
      );

      if (response) {
        this.handleSuccessfulUpdate(response);
      }

      loading.remove(this);
    }

    async addAddOnToCart(currentTarget) {
      const { addOnId, multiplier, parentLineKey, quantity } =
        currentTarget.dataset;
      const newQuantity = quantity * multiplier;

      const body = {
        items: [
          {
            id: addOnId,
            quantity: newQuantity,
            parent_line_key: parentLineKey,
          },
        ],
        ...this.asyncSectionsBody,
      };

      const options = {
        ...WYATT.config.fetch.post,
        ...{ body: JSON.stringify(body) },
      };

      loading.add(this);

      const response = await asyncRequest(WYATT.routes.cart_add_url, options, {
        messageOptions: this.asyncMessageOptions,
      });

      if (response) {
        this.handleSuccessfulUpdate(response);
      }

      loading.remove(this);
    }

    removeAddOnFromCart(currentTarget) {
      const { addOnLineKey } = currentTarget.dataset;
      const updates = { [addOnLineKey]: 0 };
      this.updateItem(updates);
    }

    handleSuccessfulUpdate(state) {
      const parsedState = JSON.parse(state);

      WYATT.shopify.cart = parsedState;

      this.publish(
        WYATT.PubSub.events.updateDOM,
        this.SECTIONS_TO_RENDER.map(({ liveRegion, name }) => ({
          liveRegion,
          html: parsedState.sections[name],
        })),
      );
    }
  },
);
